import { Component, effect, inject, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { Story } from '../../../models/story';
import { StoryStore } from '../../../stores/story/story.state';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { NgForOf, NgIf } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { TagDropdownComponent } from '../../atoms/tag-dropdown/tag-dropdown.component';
import { PickListModule } from 'primeng/picklist';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { TagModule } from 'primeng/tag';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { Bookmark } from '../../../models/bookmark';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { HeaderComponent } from '../../atoms/header/header.component';
import { TagListComponent } from '../../molecules/tag-list/tag-list.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { BookmarkCategoryHelperService } from '../../../services/bookmark-category/bookmark-category-helper.service';

import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
import { TagSummaryPipe } from '../../../pipes/tag-summary.pipe';
import { TooltipModule } from 'primeng/tooltip';
@Component({
  selector: 'app-story-form',
  standalone: true,
  imports: [
    CardModule,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputSwitchModule,
    InputTextModule,
    NgIf,
    NgForOf,
    ReactiveFormsModule,
    SharedModule,
    TagDropdownComponent,
    PickListModule,
    TagModule,
    InputTextareaModule,
    HeaderComponent,
    TagListComponent,
    FormFooterComponent,
    PickListHeaderComponent,
    TagSummaryPipe,
    TooltipModule,
  ],
  templateUrl: './story-form.component.html',
  styleUrl: './story-form.component.scss',
})
export class StoryFormComponent extends BaseFormComponent<Story> {
  readonly store = inject(StoryStore);
  readonly categoryStore = inject(BookmarkCategoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly bookmarkCategoryHelper = inject(BookmarkCategoryHelperService);
  override itemToEdit?: Story;
  bookmarks: Bookmark[] = [];
  private picklistInitialized = false;
  private picklistBuiltForId: string | undefined;
  storyBookmarks: Bookmark[] = [];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    // build the pick list as soon as the bookmarks are loaded
    effect(() => {
      if (this.bookmarkStore.initialized()) {
        untracked(() => this.buildPickList());
      }
    });
  }

  /**
   * Initializes the form for editing or creating an item. It sets up the form group with default or existing values,
   * filters bookmarks to exclude those already associated with the item being edited, and processes story bookmarks.
   * @return {void} This method does not return a value.
   */
  initializeForm(): void {
    this.buildPickList();

    this.form = this.fb.group({
      title: [this.itemToEdit?.title || '', Validators.required],
      description: [this.itemToEdit?.description || ''],
      category: [this.itemToEdit?.category || undefined],
      published: [this.itemToEdit?.published || false],
      tags: [this.itemToEdit?.tags || []],
    });

    this.formInitialized = true;
  }

  /**
   * Titles of the bookmark categories a bookmark belongs to, for the pick-list meta line.
   *
   * @param {Bookmark} bookmark - The bookmark.
   * @return {string} Comma separated titles.
   */
  bookmarkCategoryTitles(bookmark: Bookmark): string {
    return this.bookmarkCategoryHelper
      .getCategoriesForBookmark(bookmark._id)
      .map(id => this.categoryStore.getById(id)?.title)
      .filter(title => !!title)
      .join(', ');
  }

  /**
   * Builds source and target of the bookmark pick list for the edited story.
   * Only rebuilds when the edited story changes, so re-runs of the form
   * initialization don't wipe out the user's in-progress selection.
   * Bookmarks which no longer exist are dropped.
   *
   * @return {void} This method does not return a value.
   */
  private buildPickList(): void {
    const targetId = this.itemToEdit?._id;
    if (
      !this.bookmarkStore.initialized() ||
      (this.picklistInitialized && this.picklistBuiltForId === targetId)
    ) {
      return;
    }
    this.picklistInitialized = true;
    this.picklistBuiltForId = targetId;

    const items = this.bookmarkStore.items();
    this.storyBookmarks = (this.itemToEdit?.bookmarks || [])
      .map(id => items[id])
      .filter((bookmark): bookmark is Bookmark => !!bookmark);
    const assigned = new Set(this.storyBookmarks.map(bookmark => bookmark._id));
    this.bookmarks = this.bookmarkStore
      .asArray()
      .filter(bookmark => !assigned.has(bookmark._id));
  }

  /**
   * Handles the submission of the form by processing its current value,
   * appending the necessary data, and then submitting the processed form value.
   *
   * @return {void} Does not return a value.
   */
  onSubmit(): void {
    const formValue: any = this.form.value;
    formValue.bookmarks = this.storyBookmarks.map(bookmark => bookmark._id);
    this.submit(formValue);
  }
}
